import {
  CAMPAIGN_SWITCH_BASE_TIMEOUT_MS,
  MACRO_WAIT_TIMEOUT_CAP_MARGIN_MS,
  MACRO_WAIT_TIMEOUT_CAP_MS,
  type MacroCaptureResult,
  type MacroWalkResponsePayload,
} from '@playcastdotio/Messaging';
import type { MacroStepCaptureResult } from '../../../services/playjector/interfaces/IPlayjectorService';
import { classifyWaitCaptureError } from '../helpers';

/** Maximum console lines emitted per Wait node (audit item 12). */
const MAX_WAIT_POLL_LOG_LINES = 3;

function waitMs(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

export type WaitPollOutcome = 'sent-request' | 'restarted' | 'legacy-fallback' | 'stale';

export interface MacroWaitPollState {
  waitNodeId: string | null;
  waitDeadlineMs: number | null;
}

export interface MacroWaitPollCallbacks {
  isCurrentLaunch(launchInstanceId: string): boolean;
  onRestart(reason: string): Promise<void>;
  onLegacyFallback(reason: string): void;
  requestCapture(): Promise<Record<string, MacroStepCaptureResult>>;
  sendWalkRequest(captures: MacroCaptureResult[], waitMatchCurrent?: boolean): void;
  updateWalkNode(nextNodeId: string | null): void;
}

export interface MacroWaitPollContext {
  nodeId: string;
  launchInstanceId: string;
  nextNodeId: string;
  waitPoll: NonNullable<MacroWalkResponsePayload['waitPoll']>;
  captures: MacroWalkResponsePayload['captures'];
  buildCapturesFromHashes(hashes: Record<string, MacroStepCaptureResult>): MacroCaptureResult[];
}

/**
 * Owns the Wait-node poll loop and therefore the authoritative answer to
 * "is the coordinator currently waiting?".
 */
export class MacroWaitPollManager {
  private waiting = false;
  private waitPollIndex = 0;
  private pollLogCount = 0;
  private deadlineMs: number | null = null;
  private waitNodeId: string | null = null;

  public isWaiting(): boolean {
    return this.waiting;
  }

  public deadlineElapsed(): boolean {
    return this.deadlineMs !== null && Date.now() >= this.deadlineMs;
  }

  public clear(): void {
    this.waiting = false;
    this.waitPollIndex = 0;
    this.pollLogCount = 0;
    this.deadlineMs = null;
    this.waitNodeId = null;
  }

  public getState(): MacroWaitPollState {
    return {
      waitNodeId: this.waitNodeId,
      waitDeadlineMs: this.deadlineMs,
    };
  }

  /**
   * Caps a requested wait timeout so it cannot exceed campaign_switch_timeout.
   */
  public static capWaitTimeoutMs(requestedMs: number): number {
    const cap = MACRO_WAIT_TIMEOUT_CAP_MS;
    if (requestedMs > cap) {
      console.warn(
        '[StatefulMacroSystemStep] Wait timeout capped below campaign switch timeout',
        { requestedMs, cappedMs: cap, campaignSwitchTimeoutMs: CAMPAIGN_SWITCH_BASE_TIMEOUT_MS },
      );
    }
    return Math.min(requestedMs, cap);
  }

  public beginWaitNode(nodeId: string, timeoutMs: number): void {
    const cappedTimeout = MacroWaitPollManager.capWaitTimeoutMs(timeoutMs);
    this.waitPollIndex = 0;
    this.pollLogCount = 0;
    this.waitNodeId = nodeId;
    this.deadlineMs = Date.now() + cappedTimeout;
    this.waiting = true;
  }

  public async pollUntilRequestOrTerminal(
    ctx: MacroWaitPollContext,
    callbacks: MacroWaitPollCallbacks,
  ): Promise<WaitPollOutcome> {
    this.waiting = true;

    while (callbacks.isCurrentLaunch(ctx.launchInstanceId)) {
      if (this.deadlineElapsed()) {
        this.waiting = false;
        await callbacks.onRestart('wait_timeout');
        return 'restarted';
      }

      this.waitPollIndex += 1;
      if (this.pollLogCount < MAX_WAIT_POLL_LOG_LINES) {
        this.pollLogCount += 1;
        const deadlineMs = this.deadlineMs;
        console.log('[StatefulMacroSystemStep] Wait capture', {
          pollIndex: this.waitPollIndex,
          elapsedMs: deadlineMs === null ? 0 : Date.now() - (deadlineMs - ctx.waitPoll.timeoutMs),
          nextNodeId: ctx.nextNodeId,
          launchInstanceId: ctx.launchInstanceId,
        });
      }

      if (ctx.captures.length === 0) {
        await waitMs(ctx.waitPoll.intervalMs);
        continue;
      }

      let hashes: Record<string, MacroStepCaptureResult>;
      try {
        hashes = await callbacks.requestCapture();
      } catch (err) {
        if (!callbacks.isCurrentLaunch(ctx.launchInstanceId)) {
          this.waiting = false;
          return 'stale';
        }
        const action = classifyWaitCaptureError(err);
        if (action === 'restart') {
          this.waiting = false;
          await callbacks.onRestart('wait_no_process');
          return 'restarted';
        }
        if (action === 'legacy') {
          this.waiting = false;
          callbacks.onLegacyFallback('wait_requestMacroStep_failed');
          return 'legacy-fallback';
        }
        if (this.deadlineElapsed()) {
          this.waiting = false;
          await callbacks.onRestart('wait_timeout');
          return 'restarted';
        }
        await waitMs(ctx.waitPoll.intervalMs);
        continue;
      }

      if (!callbacks.isCurrentLaunch(ctx.launchInstanceId)) {
        this.waiting = false;
        return 'stale';
      }

      const hashEntries = Object.entries(hashes);
      if (hashEntries.length === 0) {
        this.waiting = false;
        callbacks.onLegacyFallback('wait_empty_variant_results');
        return 'legacy-fallback';
      }

      callbacks.updateWalkNode(ctx.nextNodeId);
      callbacks.sendWalkRequest(
        ctx.buildCapturesFromHashes(hashes),
        ctx.waitPoll.matchCurrent === true,
      );
      this.waiting = false;
      return 'sent-request';
    }

    this.waiting = false;
    return 'stale';
  }
}
